import { createContext, type ReactNode, useEffect, useMemo, useState } from "react";
import {
    attachAudioPlayerMediaSession,
    attachAudioPlayerPersistence,
    createAudioPlayer,
    createLocalStoragePersistenceAdapter,
    type AudioPlayer,
    type AudioSource,
} from "@yetric/audioplayer";
import { playerStore } from "@/context/stores";

export interface AudioState {
    audio: AudioSource | undefined;
    isPlaying: boolean;
    setAudio: (source: AudioSource) => void;
    play: () => void;
    pause: () => void;
    seek: (time?: number) => number;
    duration: () => number;
    rate: (speed?: number) => number;
    unload: () => void;
}

export const AudioContext = createContext<AudioState>({} as AudioState);

const AUDIO_PLAYER_PERSISTENCE_KEY = "hp_audioplayer_state";

export const AudioProvider = ({ children }: { children: ReactNode }) => {
    const [player] = useState<AudioPlayer>(() => createAudioPlayer());
    const [playerState, setPlayerState] = useState(() => player.getState());

    useEffect(() => {
        const persistence = attachAudioPlayerPersistence(player, {
            adapter: createLocalStoragePersistenceAdapter(AUDIO_PLAYER_PERSISTENCE_KEY),
            autoRestore: false,
        });
        const mediaSession = attachAudioPlayerMediaSession(player);
        const unsubscribe = player.subscribe((nextState) => {
            setPlayerState(nextState);
            playerStore.setPlayingState(nextState.status === "playing");
        });
        const unsubscribePlay = player.on("play", ({ state }) => {
            if (!playerStore.active || playerStore.isPlaying) {
                return;
            }

            playerStore.play(Math.floor(state.currentTime));
        });
        const unsubscribePause = player.on("pause", ({ state }) => {
            if (!playerStore.active || !playerStore.isPlaying) {
                return;
            }

            playerStore.pause(Math.floor(state.currentTime));
        });

        playerStore.restorePersistedSession();
        void persistence
            .restore()
            .then((restored) => {
                if (!restored) {
                    playerStore.close();
                }
            })
            .catch((error) => {
                console.error("Failed to restore persisted audio state", error);
                persistence.clear();
                playerStore.close();
            });

        return () => {
            unsubscribe();
            unsubscribePlay();
            unsubscribePause();
            persistence.destroy();
            mediaSession.destroy();
            player.destroy();
        };
    }, [player]);

    const audioState = useMemo<AudioState>(
        () => ({
            audio: playerState.currentSource ?? undefined,
            isPlaying: playerState.status === "playing",

            setAudio(source) {
                void player.load(source).catch((error) => {
                    console.error("Failed to load audio source", error);
                });
            },

            play() {
                void player.play().catch((error) => {
                    console.error("Failed to start audio playback", error);
                    playerStore.setPlayingState(false);
                });
            },

            pause() {
                player.pause();
            },

            seek(time) {
                if (time !== undefined) {
                    player.seek(time);
                }

                return player.getState().currentTime;
            },

            duration() {
                return player.getState().duration;
            },

            rate(speed) {
                if (speed !== undefined) {
                    player.setRate(speed);
                    return speed;
                }

                return player.getState().rate;
            },

            unload() {
                player.unload();
            },
        }),
        [player, playerState.currentSource, playerState.rate, playerState.status]
    );

    return <AudioContext.Provider value={audioState}>{children}</AudioContext.Provider>;
};
